import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import apiService from '../../services/api';
import { User } from '../../types';
import { ColorKey, getPricingConfig, PRICING_DEFAULTS, savePricingConfig } from '../../utils/pricingConfig';

const RANGOS = [
  { desde: 0, hasta: 10, mult: 4, label: 'Producto final (<10 kg)' },
  { desde: 10, hasta: 20, mult: 2.5, label: 'Revendedor 150% (10-19.99 kg)' },
  { desde: 20, hasta: 50, mult: 2, label: 'Revendedor 100% (20-49.99 kg)' },
  { desde: 50, hasta: 100, mult: 1.75, label: 'Revendedor 75% (50-99.99 kg)' },
  { desde: 100, hasta: 200, mult: 1.6, label: 'Revendedor 60% (100-199.99 kg)' },
  { desde: 200, hasta: Infinity, mult: 1.5, label: 'Revendedor 50% (>=200 kg)' }
];

const calcularPesoBolsa = (anchoCm: number, micrones: number) => (((0.92 * anchoCm * micrones) / 100) * 2);
const calcularFactorPack = (pesoPackG: number) => (pesoPackG > 0 && pesoPackG < 200 ? 1.25 : 1);
const normalizeIntInput = (raw: string, fallback: number) => {
  const value = String(raw || '').trim();
  if (!value) return fallback;
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, parsed);
};

const Cotizador: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [ancho, setAncho] = useState(45);
  const [largo, setLargo] = useState(60);
  const [micrones, setMicrones] = useState(23);
  const [bolsasPorPack, setBolsasPorPack] = useState(10);
  const [cantidadPacks, setCantidadPacks] = useState(10);
  const [color, setColor] = useState<ColorKey>('negro');
  const [valorDolar, setValorDolar] = useState(PRICING_DEFAULTS.valorDolar);
  const [preciosPorColor, setPreciosPorColor] = useState(PRICING_DEFAULTS.preciosPorColor);
  const [customerName, setCustomerName] = useState(query.get('name') || '');
  const [customerEmail, setCustomerEmail] = useState(query.get('email') || '');
  const [customerCompany, setCustomerCompany] = useState(query.get('company') || '');
  const [customerCuit, setCustomerCuit] = useState(query.get('cuit') || '');
  const [customerPhone, setCustomerPhone] = useState(query.get('phone') || '');
  const [customerAddress, setCustomerAddress] = useState(query.get('address') || '');
  const [clients, setClients] = useState<User[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [showNewClient, setShowNewClient] = useState(false);
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [clientError, setClientError] = useState('');
  const [quoteNote, setQuoteNote] = useState('Validez sugerida: 7 dias.');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [registerQuote, setRegisterQuote] = useState(true);
  const [rangeMode, setRangeMode] = useState<'auto' | 'manual'>('auto');
  const [manualRangeIndex, setManualRangeIndex] = useState(0);

  useEffect(() => {
    const syncPricing = async () => {
      const cfg = getPricingConfig();
      setValorDolar(cfg.valorDolar);
      setPreciosPorColor(cfg.preciosPorColor);

      try {
        const currency = await apiService.getCurrencyRates();
        const sell = Number(currency?.rates?.sell || 0);
        if (Number.isFinite(sell) && sell > 0 && sell !== cfg.valorDolar) {
          const updated = { ...cfg, valorDolar: sell };
          savePricingConfig(updated);
          setValorDolar(sell);
        }
      } catch {
        // Si falla la consulta, se usa el valor guardado.
      }
    };

    syncPricing();
  }, []);

  useEffect(() => {
    const loadNextQuoteNumber = async () => {
      if (!registerQuote) return;
      try {
        const data = await apiService.getNextQuoteNumber();
        setQuoteNumber(data.quoteNumber || '');
      } catch {
        // Silencio: puede no estar disponible temporalmente.
      }
    };
    loadNextQuoteNumber();
  }, [registerQuote]);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const data = await apiService.getUsers({ role: 'cliente', page: 1, limit: 500 });
        setClients(data.users || []);
      } catch {
        setClients([]);
      }
    };
    loadClients();
  }, []);

  const r = useMemo(() => {
    const precioKiloUSD = preciosPorColor[color] || 0;
    const peso1000m = calcularPesoBolsa(ancho, micrones);
    const pesoBolsaG = (largo / 100) * peso1000m;
    const pesoPackG = pesoBolsaG * bolsasPorPack;
    const pesoTotalKg = (pesoPackG * cantidadPacks) / 1000;
    const factorPack = calcularFactorPack(pesoPackG);
    const rangoAuto = RANGOS.find((x) => pesoTotalKg >= x.desde && pesoTotalKg < x.hasta) || RANGOS[0];
    const rangoManual = RANGOS[manualRangeIndex] || RANGOS[0];
    const rango = rangeMode === 'manual' ? rangoManual : rangoAuto;
    const mult = rango.mult;
    const costoPedidoUSD = pesoTotalKg * precioKiloUSD;
    const precioTotalUSD = costoPedidoUSD * factorPack * mult;
    const precioTotalARS = precioTotalUSD * valorDolar;
    const costoTotalARS = costoPedidoUSD * valorDolar;
    const gananciaARS = precioTotalARS - costoTotalARS;
    const margenPct = costoTotalARS > 0 ? (gananciaARS / costoTotalARS) * 100 : 0;
    const bolsasTotales = cantidadPacks * bolsasPorPack;
    const costoPorPackARS = cantidadPacks > 0 ? (costoTotalARS / cantidadPacks) : 0;
    const descuentoPct = (mult < 2.5 && pesoTotalKg >= 20) ? Math.round((1 - mult / 2.5) * 100) : 0;

    return {
      rango: rango.label,
      mult,
      descuentoPct,
      precioKiloUSD,
      pesoBolsaG,
      pesoPackG,
      pesoTotalKg,
      factorPack,
      precioTotalARS,
      costoTotalARS,
      costoPorPackARS,
      gananciaARS,
      margenPct,
      bolsasTotales,
      precioPorBolsa: bolsasTotales > 0 ? precioTotalARS / bolsasTotales : 0,
      precioPorPack: cantidadPacks > 0 ? precioTotalARS / cantidadPacks : 0
    };
  }, [ancho, largo, micrones, bolsasPorPack, cantidadPacks, color, valorDolar, preciosPorColor, rangeMode, manualRangeIndex]);

  const fmt = (n: number, d = 2) => n.toLocaleString('es-AR', { minimumFractionDigits: d, maximumFractionDigits: d });
  const warnings: string[] = [];
  if (r.pesoPackG > 0 && r.pesoPackG < 200) warnings.push('Pack menor a 200 g: se aplico recargo +25%.');
  if (r.precioKiloUSD <= 0) warnings.push('Precio USD/kg en 0 para el color seleccionado.');
  if (r.gananciaARS < 0) warnings.push('Ganancia negativa: revisar precio USD/kg o rango.');

  const applyClient = (client: User) => {
    setSelectedClientId(String(client.id));
    setCustomerName(client.name || '');
    setCustomerEmail(client.email || '');
    setCustomerCompany(client.company || '');
    setCustomerCuit(client.cuit || client.taxId || '');
    setCustomerPhone(client.phone || '');
    setCustomerAddress(client.address || '');
    setClientError('');
    setShowNewClient(false);
  };

  const handleClientSelect = (value: string) => {
    if (value === 'new') {
      setSelectedClientId('new');
      setShowNewClient(true);
      setClientError('');
      return;
    }
    if (!value) {
      setSelectedClientId('');
      return;
    }
    const client = clients.find((x) => String(x.id) === value);
    if (client) applyClient(client);
  };

  const saveClient = async () => {
    const name = customerName.trim();
    const email = customerEmail.trim();
    if (!name || !email) {
      setClientError('Completa nombre y email para guardar el cliente.');
      return;
    }
    try {
      setIsSavingClient(true);
      setClientError('');
      const data = await apiService.createUserByAdmin({
        name,
        email,
        company: customerCompany.trim(),
        cuit: customerCuit.trim(),
        phone: customerPhone.trim(),
        address: customerAddress.trim(),
        role: 'cliente',
        isActive: true
      });
      setClients((current) => [data.user, ...current.filter((x) => x.id !== data.user.id)]);
      applyClient(data.user);
    } catch (error: any) {
      setClientError(error?.response?.data?.detail || error?.message || 'No se pudo guardar el cliente.');
    } finally {
      setIsSavingClient(false);
    }
  };

  const generatePdf = async () => {
    try {
      setIsGeneratingPdf(true);
      const cfg = getPricingConfig();
      const { blob, filename } = await apiService.generateQuotePdf({
        quoteNumber: quoteNumber.trim(),
        registerQuote,
        customerName,
        customerEmail,
        customerCompany,
        customerCuit,
        customerPhone,
        customerAddress,
        note: quoteNote,
        anchoCm: ancho,
        largoCm: largo,
        micrones,
        bolsasPorPack,
        cantidadPacks,
        color,
        precioPorBolsaARS: r.precioPorBolsa,
        precioPorPackARS: r.precioPorPack,
        precioTotalARS: r.precioTotalARS,
        costoTotalARS: r.costoTotalARS,
        margenPct: r.margenPct,
        template: cfg.pdfTemplate
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      if (registerQuote) {
        try {
          const next = await apiService.getNextQuoteNumber();
          setQuoteNumber(next.quoteNumber || '');
        } catch {
          // Sin bloqueo de UI
        }
      }
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
  const readOnlyCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-600';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const activeRangeIndex = RANGOS.findIndex((x) => x.label === r.rango);
  const colorSwatch: Record<ColorKey, string> = {
    negro: 'bg-gray-900',
    blanco: 'bg-white border border-gray-300',
    azul: 'bg-blue-600',
    verde: 'bg-green-600',
    rojo: 'bg-red-600'
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Cotizador Web</h1>
          <p className="text-sm text-gray-600">Precios y dolar definidos en Admin &gt; Configuracion</p>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">Dolar: <strong>${fmt(valorDolar)}</strong></span>
          {quoteNumber && <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-700">N° <strong>{quoteNumber}</strong></span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-6">
          {/* Producto */}
          <section className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-5">
            <h2 className="font-semibold text-gray-900">1. Producto</h2>

            <div>
              <div className="text-sm font-medium text-gray-800 mb-2">Medidas</div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className={labelCls}>Ancho (cm)</label><input className={inputCls} type="number" value={ancho} onChange={(e) => setAncho(Number(e.target.value))} /></div>
                <div><label className={labelCls}>Largo (cm)</label><input className={inputCls} type="number" value={largo} onChange={(e) => setLargo(Number(e.target.value))} /></div>
                <div><label className={labelCls}>Micrones</label><input className={inputCls} type="number" value={micrones} onChange={(e) => setMicrones(Number(e.target.value))} /></div>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-800 mb-2">Cantidad</div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Bolsas por pack</label><input className={inputCls} type="number" value={bolsasPorPack} onChange={(e) => setBolsasPorPack(Number(e.target.value))} onBlur={(e) => { const n = normalizeIntInput(e.currentTarget.value, bolsasPorPack); e.currentTarget.value = String(n); setBolsasPorPack(n); }} /></div>
                <div><label className={labelCls}>Cantidad de packs</label><input className={inputCls} type="number" value={cantidadPacks} onChange={(e) => setCantidadPacks(Number(e.target.value))} onBlur={(e) => { const n = normalizeIntInput(e.currentTarget.value, cantidadPacks); e.currentTarget.value = String(n); setCantidadPacks(n); }} /></div>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-800 mb-2">Color <span className="text-xs font-normal text-gray-500">(precio USD/kg)</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {(Object.keys(preciosPorColor) as ColorKey[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setColor(k)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${color === k ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    <span className={`h-4 w-4 shrink-0 rounded-full ${colorSwatch[k] || 'bg-gray-400'}`} />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium capitalize text-gray-900">{k}</span>
                      <span className="block text-xs text-gray-500">US$ {fmt(Number(preciosPorColor[k] || 0))}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-800 mb-2">Rango de precio</div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="inline-flex rounded-lg border border-gray-300 p-1 text-sm self-start">
                  <button type="button" onClick={() => setRangeMode('auto')} className={`px-3 py-1.5 rounded-md ${rangeMode === 'auto' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>Automático por peso</button>
                  <button type="button" onClick={() => setRangeMode('manual')} className={`px-3 py-1.5 rounded-md ${rangeMode === 'manual' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>Manual</button>
                </div>
                {rangeMode === 'manual' ? (
                  <select className={`${inputCls} sm:flex-1`} value={manualRangeIndex} onChange={(e) => setManualRangeIndex(Number(e.target.value))}>
                    {RANGOS.map((x, i) => (
                      <option key={i} value={i}>{x.label} · x{x.mult}</option>
                    ))}
                  </select>
                ) : (
                  <div className="text-sm text-gray-600 sm:self-center">{r.rango} · x{fmt(r.mult, 2)}</div>
                )}
              </div>
            </div>
          </section>

          {/* Cliente */}
          <section className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <h2 className="font-semibold text-gray-900">2. Cliente</h2>
            <div>
              <label className={labelCls}>Buscar cliente</label>
              <select className={inputCls} value={selectedClientId} onChange={(e) => handleClientSelect(e.target.value)}>
                <option value="">Seleccionar cliente</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {(client.company || client.name)} - {client.email}
                  </option>
                ))}
                <option value="new">+ Agregar nuevo cliente</option>
              </select>
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${showNewClient ? 'border border-dashed border-primary-300 bg-primary-50 rounded-lg p-4' : ''}`}>
              {showNewClient && <div className="sm:col-span-2 text-sm font-medium text-primary-700">Nuevo cliente</div>}
              <div><label className={labelCls}>Nombre</label><input className={inputCls} value={customerName} onChange={(e) => setCustomerName(e.target.value)} /></div>
              <div><label className={labelCls}>Empresa</label><input className={inputCls} value={customerCompany} onChange={(e) => setCustomerCompany(e.target.value)} /></div>
              <div><label className={labelCls}>CUIT</label><input className={inputCls} value={customerCuit} onChange={(e) => setCustomerCuit(e.target.value)} /></div>
              <div><label className={labelCls}>Telefono</label><input className={inputCls} value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} /></div>
              <div><label className={labelCls}>Email</label><input className={inputCls} value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} /></div>
              <div><label className={labelCls}>Direccion</label><input className={inputCls} value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} /></div>
              {showNewClient && (
                <>
                  {clientError && <div className="sm:col-span-2 text-sm text-red-600">{clientError}</div>}
                  <div className="sm:col-span-2 flex gap-3">
                    <button
                      type="button"
                      onClick={saveClient}
                      disabled={isSavingClient}
                      className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                    >
                      {isSavingClient ? 'Guardando...' : 'Guardar cliente'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowNewClient(false); setSelectedClientId(''); setClientError(''); }}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Tabla de rangos */}
          <details className="bg-white rounded-lg shadow p-4 sm:p-6">
            <summary className="cursor-pointer font-semibold text-gray-900 select-none">Tabla de rangos</summary>
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b text-gray-600">
                    <th className="py-2 px-2">Rango kg</th>
                    <th className="py-2 px-2">Tipo</th>
                    <th className="py-2 px-2">Multiplicador</th>
                    <th className="py-2 px-2">Descuento</th>
                  </tr>
                </thead>
                <tbody>
                  {RANGOS.map((x, i) => {
                    const desc = x.desde >= 20 ? Math.round((1 - x.mult / 2.5) * 100) : 0;
                    return (
                      <tr key={i} className={`border-b last:border-0 ${i === activeRangeIndex ? 'bg-primary-50 font-medium text-primary-800' : ''}`}>
                        <td className="py-2 px-2">{x.hasta === Infinity ? `${x.desde}+` : `${x.desde} - ${x.hasta - 0.01}`}</td>
                        <td className="py-2 px-2">{x.label.includes('Producto final') ? 'Producto final' : 'Revendedor'}</td>
                        <td className="py-2 px-2">x{x.mult}</td>
                        <td className="py-2 px-2">{desc}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        </div>

        {/* Resumen */}
        <aside className="space-y-4 lg:sticky lg:top-4">
          <section className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-primary-600 text-white p-4 sm:p-5">
              <div className="text-sm opacity-90">Total sugerido</div>
              <div className="text-2xl sm:text-3xl font-bold">${fmt(r.precioTotalARS)}</div>
              <div className="text-xs opacity-90 mt-1">ARS · {fmt(r.pesoTotalKg)} kg · {fmt(r.bolsasTotales, 0)} bolsas</div>
            </div>
            <div className="grid grid-cols-2 divide-x border-b text-center">
              <div className="p-3">
                <div className="text-xs text-gray-500">Por pack</div>
                <div className="font-semibold text-gray-900">${fmt(r.precioPorPack)}</div>
              </div>
              <div className="p-3">
                <div className="text-xs text-gray-500">Por bolsa</div>
                <div className="font-semibold text-gray-900">${fmt(r.precioPorBolsa)}</div>
              </div>
            </div>
            <dl className="p-4 sm:p-5 space-y-2 text-sm">
              <div className="flex justify-between gap-2"><dt className="text-gray-500">Rango</dt><dd className="text-right text-gray-900">{r.rango}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-gray-500">Multiplicador</dt><dd className="text-gray-900">x{fmt(r.mult, 2)} {rangeMode === 'manual' ? '(manual)' : ''}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-gray-500">Descuento</dt><dd className="text-gray-900">{r.descuentoPct}%</dd></div>
              <div className="border-t pt-2 flex justify-between gap-2"><dt className="text-gray-500">Peso bolsa</dt><dd className="text-gray-900">{fmt(r.pesoBolsaG)} g</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-gray-500">Peso pack</dt><dd className="text-gray-900">{fmt(r.pesoPackG)} g {r.factorPack > 1 ? '(+25%)' : ''}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-gray-500">Peso total</dt><dd className="text-gray-900">{fmt(r.pesoTotalKg)} kg</dd></div>
              <div className="border-t pt-2 flex justify-between gap-2"><dt className="text-gray-500">Costo por pack</dt><dd className="text-gray-900">${fmt(r.costoPorPackARS)}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-gray-500">Costo total</dt><dd className="text-gray-900">${fmt(r.costoTotalARS)}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-gray-500">Ganancia</dt><dd className={r.gananciaARS < 0 ? 'text-red-600 font-medium' : 'text-green-700 font-medium'}>${fmt(r.gananciaARS)}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-gray-500">Margen</dt><dd className="text-gray-900">{fmt(r.margenPct)}%</dd></div>
            </dl>
          </section>

          {!!warnings.length && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-sm text-yellow-900 space-y-1">
              {warnings.map((w, i) => <div key={i}>• {w}</div>)}
            </div>
          )}

          <section className="bg-white rounded-lg shadow p-4 sm:p-5 space-y-3">
            <h2 className="font-semibold text-gray-900">3. Cotización</h2>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary-600" checked={registerQuote} onChange={(e) => setRegisterQuote(e.target.checked)} />
              Registrar cotización
            </label>
            <div><label className={labelCls}>N° cotización</label><input className={inputCls} value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)} placeholder={registerQuote ? '2026-001' : 'BORRADOR-20260421-1200'} /></div>
            <div><label className={labelCls}>Nota</label><textarea className={inputCls} rows={2} value={quoteNote} onChange={(e) => setQuoteNote(e.target.value)} /></div>
            <div><label className={labelCls}>Valor dolar (ARS)</label><input className={readOnlyCls} type="number" value={valorDolar} readOnly /></div>
            <button
              onClick={generatePdf}
              disabled={isGeneratingPdf}
              className="w-full px-4 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-60"
            >
              {isGeneratingPdf ? 'Generando PDF...' : registerQuote ? 'Registrar y Generar PDF' : 'Generar PDF sin registrar'}
            </button>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default Cotizador;
